import { useState, useEffect } from 'react'
import { Hash, Volume2, Film, Plus, Trash2, Edit2, ChevronDown, ChevronRight } from 'lucide-react'
import { supabase, formatErrorMessage } from '../lib/supabase'
import { useToast } from '../hooks/useToast'

/**
 * Channel Management Component
 * Allows space owners/admins to create, edit, and delete channels
 * Organized by type: Text, Voice, Media
 */
export default function ChannelManagement({ space, onChannelCreated }) {
  const toast = useToast()
  const [channels, setChannels] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createType, setCreateType] = useState('text') // 'text', 'voice', 'media'
  const [newChannelName, setNewChannelName] = useState('')
  const [newChannelDescription, setNewChannelDescription] = useState('')
  const [newChannelPersistent, setNewChannelPersistent] = useState(false)
  const [editingChannel, setEditingChannel] = useState(null)
  const [deletingChannel, setDeletingChannel] = useState(null)

  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    text: true,
    voice: true,
    media: true
  })

  useEffect(() => {
    if (space) {
      loadChannels()
    }
  }, [space])

  const loadChannels = async () => {
    if (!space) return

    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('channels')
        .select('*')
        .eq('space_id', space.id)
        .order('sort_order', { ascending: true })

      if (error) throw error
      setChannels(data || [])
    } catch (error) {
      console.error('Error loading channels:', error)
      toast.error(formatErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleCreateChannel = async (e) => {
    e.preventDefault()
    if (!newChannelName.trim()) {
      toast.error('Channel name is required')
      return
    }

    try {
      // Find next sort_order for this type within the space
      const { data: topSort, error: sortErr } = await supabase
        .from('channels')
        .select('sort_order')
        .eq('space_id', space.id)
        .eq('type', createType)
        .order('sort_order', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (sortErr) {
        console.error('Error getting sort order:', sortErr)
        throw new Error('Could not determine channel order')
      }

      const nextSort = (topSort?.sort_order || 0) + 1

      // Insert the new channel directly via Supabase
      const { data: channel, error: insertErr } = await supabase
        .from('channels')
        .insert({
          space_id: space.id,
          name: newChannelName.trim(),
          description: newChannelDescription.trim() || null,
          type: createType,
          is_persistent: newChannelPersistent,
          sort_order: nextSort,
        })
        .select('*')
        .single()

      if (insertErr) {
        console.error('Error creating channel:', insertErr)
        throw new Error(insertErr.message || 'Failed to create channel')
      }

      if (!channel) {
        console.error('Channel creation returned no data - likely RLS policy blocking insert')
        throw new Error('Failed to create channel - permission denied')
      }

      setChannels([...channels, channel])
      toast.success(`${getChannelTypeLabel(createType)} channel created!`)
      setShowCreateModal(false)
      setNewChannelName('')
      setNewChannelDescription('')
      setNewChannelPersistent(false)

      if (onChannelCreated) {
        onChannelCreated(channel)
      }
    } catch (error) {
      console.error('Error creating channel:', error)
      toast.error(formatErrorMessage(error))
    }
  }

  const handleUpdateChannel = async (channel) => {
    if (!editingChannel) return

    try {
      const { data, error } = await supabase
        .from('channels')
        .update({
          name: editingChannel.name,
          description: editingChannel.description,
          is_persistent: editingChannel.is_persistent
        })
        .eq('id', channel.id)
        .select()
        .single()

      if (error) throw error

      setChannels(channels.map(c => c.id === data.id ? data : c))
      toast.success('Channel updated!')
      setEditingChannel(null)
    } catch (error) {
      console.error('Error updating channel:', error)
      toast.error(formatErrorMessage(error))
    }
  }

  const handleDeleteChannel = async (channel) => {
    try {
      const { error } = await supabase
        .from('channels')
        .delete()
        .eq('id', channel.id)

      if (error) throw error

      setChannels(channels.filter(c => c.id !== channel.id))
      toast.success('Channel deleted')
      setDeletingChannel(null)
    } catch (error) {
      console.error('Error deleting channel:', error)
      toast.error(formatErrorMessage(error))
    }
  }

  const getChannelTypeLabel = (type) => {
    switch (type) {
      case 'text': return 'Text'
      case 'voice': return 'Voice'
      case 'media': return 'Media'
      default: return 'Unknown'
    }
  }

  const getChannelIcon = (type) => {
    switch (type) {
      case 'text': return Hash
      case 'voice': return Volume2
      case 'media': return Film
      default: return Hash
    }
  }

  const toggleSection = (type) => {
    setExpandedSections(prev => ({
      ...prev,
      [type]: !prev[type]
    }))
  }

  const channelsByType = {
    text: channels.filter(c => c.type === 'text'),
    voice: channels.filter(c => c.type === 'voice'),
    media: channels.filter(c => c.type === 'media')
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center text-[#949BA4]">Loading channels...</div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white">Channel Management</h3>
        <button
          type="button"
          onClick={() => {
            setShowCreateModal(true)
            setCreateType('text')
          }}
          className="px-4 py-2 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded text-sm font-medium transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Channel
        </button>
      </div>

      <p className="text-sm text-[#949BA4] mb-6">
        Organize your space with different channel types for text chat, voice calls, and media sharing.
      </p>

      {/* Text Channels */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => toggleSection('text')}
          className="w-full flex items-center justify-between p-3 bg-[#2B2D31] rounded-t-lg hover:bg-[#35373C] transition-colors"
        >
          <div className="flex items-center gap-2">
            <ChevronRight className={`w-4 h-4 text-[#949BA4] transition-transform ${expandedSections.text ? 'rotate-90' : ''}`} />
            <Hash className="w-5 h-5 text-[#B5BAC1]" />
            <span className="text-sm font-semibold text-white uppercase tracking-wide">
              Text Channels ({channelsByType.text.length})
            </span>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setCreateType('text')
              setShowCreateModal(true)
            }}
            className="px-3 py-1 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded text-xs font-medium transition-colors"
          >
            + Create
          </button>
        </button>

        {expandedSections.text && (
          <div className="bg-[#2B2D31] rounded-b-lg p-3">
            {channelsByType.text.length === 0 ? (
              <p className="text-sm text-[#949BA4] text-center py-4">
                No text channels yet. Create one to get started!
              </p>
            ) : (
              <div className="space-y-2">
                {channelsByType.text.map(channel => (
                  <ChannelRow
                    key={channel.id}
                    channel={channel}
                    isEditing={editingChannel?.id === channel.id}
                    editData={editingChannel}
                    onEdit={setEditingChannel}
                    onSave={handleUpdateChannel}
                    onCancelEdit={() => setEditingChannel(null)}
                    onDelete={() => setDeletingChannel(channel)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Voice Channels */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => toggleSection('voice')}
          className="w-full flex items-center justify-between p-3 bg-[#2B2D31] rounded-t-lg hover:bg-[#35373C] transition-colors"
        >
          <div className="flex items-center gap-2">
            <ChevronRight className={`w-4 h-4 text-[#949BA4] transition-transform ${expandedSections.voice ? 'rotate-90' : ''}`} />
            <Volume2 className="w-5 h-5 text-[#B5BAC1]" />
            <span className="text-sm font-semibold text-white uppercase tracking-wide">
              Voice Channels ({channelsByType.voice.length})
            </span>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setCreateType('voice')
              setShowCreateModal(true)
            }}
            className="px-3 py-1 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded text-xs font-medium transition-colors"
          >
            + Create
          </button>
        </button>

        {expandedSections.voice && (
          <div className="bg-[#2B2D31] rounded-b-lg p-3">
            {channelsByType.voice.length === 0 ? (
              <p className="text-sm text-[#949BA4] text-center py-4">
                No voice channels yet. Create one for audio calls!
              </p>
            ) : (
              <div className="space-y-2">
                {channelsByType.voice.map(channel => (
                  <ChannelRow
                    key={channel.id}
                    channel={channel}
                    isEditing={editingChannel?.id === channel.id}
                    editData={editingChannel}
                    onEdit={setEditingChannel}
                    onSave={handleUpdateChannel}
                    onCancelEdit={() => setEditingChannel(null)}
                    onDelete={() => setDeletingChannel(channel)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Media Channels */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => toggleSection('media')}
          className="w-full flex items-center justify-between p-3 bg-[#2B2D31] rounded-t-lg hover:bg-[#35373C] transition-colors"
        >
          <div className="flex items-center gap-2">
            <ChevronRight className={`w-4 h-4 text-[#949BA4] transition-transform ${expandedSections.media ? 'rotate-90' : ''}`} />
            <Film className="w-5 h-5 text-[#B5BAC1]" />
            <span className="text-sm font-semibold text-white uppercase tracking-wide">
              Media Channels ({channelsByType.media.length})
            </span>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setCreateType('media')
              setShowCreateModal(true)
            }}
            className="px-3 py-1 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded text-xs font-medium transition-colors"
          >
            + Create
          </button>
        </button>

        {expandedSections.media && (
          <div className="bg-[#2B2D31] rounded-b-lg p-3">
            {channelsByType.media.length === 0 ? (
              <p className="text-sm text-[#949BA4] text-center py-4">
                No media channels yet. Create one for watch parties!
              </p>
            ) : (
              <div className="space-y-2">
                {channelsByType.media.map(channel => (
                  <ChannelRow
                    key={channel.id}
                    channel={channel}
                    isEditing={editingChannel?.id === channel.id}
                    editData={editingChannel}
                    onEdit={setEditingChannel}
                    onSave={handleUpdateChannel}
                    onCancelEdit={() => setEditingChannel(null)}
                    onDelete={() => setDeletingChannel(channel)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Channel Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-[#313338] rounded-lg shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              Create {getChannelTypeLabel(createType)} Channel
            </h3>

            <form onSubmit={handleCreateChannel} className="space-y-4">
              {/* Channel Type Selector */}
              <div>
                <label className="block text-sm font-medium text-[#B5BAC1] mb-2">
                  Channel Type
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['text', 'voice', 'media'].map(type => {
                    const Icon = getChannelIcon(type)
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setCreateType(type)}
                        className={`p-3 rounded flex flex-col items-center gap-2 transition-colors ${
                          createType === type
                            ? 'bg-[#5865F2] text-white'
                            : 'bg-[#2B2D31] text-[#B5BAC1] hover:bg-[#35373C]'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-xs font-medium">{getChannelTypeLabel(type)}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Channel Name */}
              <div>
                <label className="block text-sm font-medium text-[#B5BAC1] mb-2">
                  Channel Name *
                </label>
                <input
                  type="text"
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                  placeholder={`${createType}-channel`}
                  className="w-full px-3 py-2 bg-[#1E1F22] border border-[#1E1F22] rounded text-[#DBDEE1] focus:border-[#00A8FC] focus:outline-none transition-colors"
                  autoFocus
                  required
                />
              </div>

              {/* Channel Description */}
              <div>
                <label className="block text-sm font-medium text-[#B5BAC1] mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={newChannelDescription}
                  onChange={(e) => setNewChannelDescription(e.target.value)}
                  placeholder="What's this channel for?"
                  rows={3}
                  className="w-full px-3 py-2 bg-[#1E1F22] border border-[#1E1F22] rounded text-[#DBDEE1] focus:border-[#00A8FC] focus:outline-none transition-colors resize-none"
                />
              </div>

              {/* Persistent Chat Toggle */}
              <div>
                <label className="flex items-center justify-between p-3 bg-[#2B2D31] rounded hover:bg-[#35373C] transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-[#00A8FC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    <div>
                      <div className="text-sm font-medium text-white">Persistent Chat</div>
                      <div className="text-xs text-[#949BA4]">Messages remain after voice/video calls end</div>
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={newChannelPersistent}
                      onChange={(e) => setNewChannelPersistent(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-[#4E5058] rounded-full peer peer-checked:bg-[#3BA55D] transition-colors" />
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                  </div>
                </label>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded text-sm font-medium transition-colors"
                >
                  Create Channel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setNewChannelName('')
                    setNewChannelDescription('')
                    setNewChannelPersistent(false)
                  }}
                  className="px-4 py-2 bg-[#4E5058] hover:bg-[#5C5E66] text-white rounded text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingChannel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-[#313338] rounded-lg shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Delete Channel?</h3>
            <p className="text-sm text-[#B5BAC1] mb-6">
              Are you sure you want to delete <strong>#{deletingChannel.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => handleDeleteChannel(deletingChannel)}
                className="flex-1 px-4 py-2 bg-[#ED4245] hover:bg-[#A12D30] text-white rounded text-sm font-medium transition-colors"
              >
                Delete Channel
              </button>
              <button
                onClick={() => setDeletingChannel(null)}
                className="px-4 py-2 bg-[#4E5058] hover:bg-[#5C5E66] text-white rounded text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Channel Row Component
function ChannelRow({ channel, isEditing, editData, onEdit, onSave, onCancelEdit, onDelete }) {
  const Icon = channel.type === 'text' ? Hash : channel.type === 'voice' ? Volume2 : Film

  if (isEditing) {
    return (
      <div className="p-3 bg-[#1E1F22] rounded">
        <div className="space-y-2">
          <input
            type="text"
            value={editData.name}
            onChange={(e) => onEdit({ ...editData, name: e.target.value })}
            className="w-full px-2 py-1 bg-[#313338] border border-[#4E5058] rounded text-[#DBDEE1] text-sm focus:border-[#00A8FC] focus:outline-none"
          />
          <input
            type="text"
            value={editData.description || ''}
            onChange={(e) => onEdit({ ...editData, description: e.target.value })}
            placeholder="Description"
            className="w-full px-2 py-1 bg-[#313338] border border-[#4E5058] rounded text-[#DBDEE1] text-sm focus:border-[#00A8FC] focus:outline-none"
          />
          {/* Persistent Chat Toggle in Edit Mode */}
          <label className="flex items-center justify-between p-2 bg-[#313338] rounded cursor-pointer">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-[#00A8FC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
              <span className="text-xs text-[#B5BAC1]">Persistent Chat</span>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={editData.is_persistent || false}
                onChange={(e) => onEdit({ ...editData, is_persistent: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-8 h-4 bg-[#4E5058] rounded-full peer peer-checked:bg-[#3BA55D] transition-colors" />
              <div className="absolute left-0.5 top-0.5 w-3 h-3 bg-white rounded-full transition-transform peer-checked:translate-x-4" />
            </div>
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => onSave(channel)}
              className="px-3 py-1 bg-[#3BA55D] hover:bg-[#2D7D46] text-white rounded text-xs font-medium transition-colors"
            >
              Save
            </button>
            <button
              onClick={onCancelEdit}
              className="px-3 py-1 bg-[#4E5058] hover:bg-[#5C5E66] text-white rounded text-xs font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between p-3 bg-[#1E1F22] rounded hover:bg-[#292B2F] transition-colors group">
      <div className="flex items-center gap-3 flex-1">
        <Icon className="w-4 h-4 text-[#949BA4]" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#DBDEE1]">#{channel.name}</span>
            {channel.is_persistent && (
              <span className="px-1.5 py-0.5 text-[10px] bg-[#00A8FC]/20 text-[#00A8FC] rounded font-medium" title="Persistent Chat">
                Persistent
              </span>
            )}
          </div>
          {channel.description && (
            <div className="text-xs text-[#949BA4] mt-0.5">{channel.description}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onEdit(channel)}
          className="p-1.5 hover:bg-[#35373C] rounded transition-colors"
          title="Edit channel"
        >
          <Edit2 className="w-4 h-4 text-[#B5BAC1]" />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 hover:bg-[#35373C] rounded transition-colors"
          title="Delete channel"
        >
          <Trash2 className="w-4 h-4 text-[#ED4245]" />
        </button>
      </div>
    </div>
  )
}
